<template>
  <div class="terrain-location-field">
    <label v-if="label" class="terrain-location-field-label">{{ label }}</label>

    <div v-if="!capture" class="terrain-location-field-empty">
      Sin captura GPS registrada.
    </div>

    <template v-else>
      <div class="terrain-location-field-row">
        <span class="terrain-location-field-key">Coordenada capturada</span>
        <span class="terrain-location-field-value">{{ formattedCoordinates(capture.captured.coordinates) }}</span>
      </div>
      <div class="terrain-location-field-row">
        <span class="terrain-location-field-key">Fecha de captura</span>
        <span class="terrain-location-field-value">{{ formattedDate(capture.captured.capturedAt) }}</span>
      </div>
      <div v-if="isManual" class="terrain-location-field-row">
        <span class="terrain-location-field-key">Origen</span>
        <span class="terrain-location-field-value">
          <span class="terrain-location-field-badge terrain-location-field-badge--manual">Manual</span>
        </span>
      </div>
      <div v-else class="terrain-location-field-row">
        <span class="terrain-location-field-key">Precisión</span>
        <span class="terrain-location-field-value">
          {{ accuracyLabel }}
          <span
            class="terrain-location-field-badge"
            :class="'terrain-location-field-badge--' + accuracyClass"
          >{{ accuracyClassText }}</span>
        </span>
      </div>

      <div v-if="isWarning" class="terrain-location-field-warning">
        La precisión GPS supera los 50 metros. Puede reintentar la captura o
        continuar con la precisión registrada.
      </div>

      <div v-if="capture.corrected" class="terrain-location-field-correction">
        <div class="terrain-location-field-row">
          <span class="terrain-location-field-key">Coordenada corregida</span>
          <span class="terrain-location-field-value">{{ formattedCoordinates(capture.corrected.coordinates) }}</span>
        </div>
        <div class="terrain-location-field-row">
          <span class="terrain-location-field-key">Fecha de corrección</span>
          <span class="terrain-location-field-value">{{ formattedDate(capture.corrected.correctedAt) }}</span>
        </div>
      </div>

      <div class="terrain-location-field-actions">
        <!--
          The manual correction action is always enabled, independent of
          the precision classification: correcting must remain available
          for any accuracy bucket (buena/aceptable/advertida).
          Requirement 11.9.
        -->
        <button
          type="button"
          class="terrain-location-field-correct-btn"
          @click="$emit('correct', capture)"
        >
          {{ capture.corrected ? 'Corregir de nuevo' : 'Corregir manualmente' }}
        </button>
        <button
          v-if="isWarning"
          type="button"
          class="terrain-location-field-retry-btn"
          @click="$emit('retry')"
        >
          Reintentar captura
        </button>
      </div>
    </template>
  </div>
</template>

<script>
// LocationField.vue displays a captured/corrected GPS_Original, GPS_Final
// or ubicación Pregunta capture (GPSCaptureDTO shape) and its
// Precisión_GPS classification to the user. It mirrors the backend
// classification thresholds implemented in
// visor-mapas-backend/services/terrain/gps.go (ClassifyGPSAccuracy):
// buena in [0,20] meters, aceptable in (20,50] meters and advertida
// (non-blocking warning) in (50, +Infinity) meters.
//
// This component is display-only: it does not capture GPS itself (that is
// GpsCapture.vue's responsibility) and does not decide whether a
// correction is accepted; it only always allows requesting one regardless
// of the current precision bucket (Requirement 11.9), preserving capture
// and correction as distinct values shown side by side (Requirement
// 11.10).
//
// Requirements: 11.4, 11.6, 11.7, 11.8, 11.9, 11.10 (spec: levantamiento-terreno)

/**
 * ACCURACY_CLASS mirrors GPSAccuracyClass in
 * visor-mapas-backend/services/terrain/gps.go.
 */
const ACCURACY_CLASS = Object.freeze({
  GOOD: "buena",
  ACCEPTABLE: "aceptable",
  WARNING: "advertida",
});

const ACCURACY_CLASS_TEXT = Object.freeze({
  [ACCURACY_CLASS.GOOD]: "Buena",
  [ACCURACY_CLASS.ACCEPTABLE]: "Aceptable",
  [ACCURACY_CLASS.WARNING]: "Advertencia",
});

/**
 * classifyGpsAccuracy classifies a Precisión_GPS value (in meters) using
 * the same thresholds as ClassifyGPSAccuracy in gps.go: buena in [0,20],
 * aceptable in (20,50], advertida in (50,+Infinity).
 *
 * @param {number} accuracyMeters
 * @returns {string} one of ACCURACY_CLASS values
 */
export function classifyGpsAccuracy(accuracyMeters) {
  if (accuracyMeters <= 20) return ACCURACY_CLASS.GOOD;
  if (accuracyMeters <= 50) return ACCURACY_CLASS.ACCEPTABLE;
  return ACCURACY_CLASS.WARNING;
}

export default {
  name: "LocationField",
  props: {
    label: {
      type: String,
      default: "",
    },
    /**
     * GPSCaptureDTO-shaped object:
     * { captured: { coordinates: [lon, lat], accuracyMeters, capturedAt },
     *   corrected?: { coordinates: [lon, lat], correctedAt } }
     */
    capture: {
      type: Object,
      default: null,
    },
  },
  computed: {
    isManual() {
      return Boolean(this.capture && this.capture.captured.manual);
    },
    accuracyMeters() {
      return this.capture ? this.capture.captured.accuracyMeters : null;
    },
    accuracyClass() {
      if (this.accuracyMeters === null || this.accuracyMeters === undefined) return "";
      return classifyGpsAccuracy(this.accuracyMeters);
    },
    accuracyClassText() {
      return ACCURACY_CLASS_TEXT[this.accuracyClass] || "";
    },
    accuracyLabel() {
      if (this.accuracyMeters === null || this.accuracyMeters === undefined) return "";
      return `${this.accuracyMeters} m`;
    },
    isWarning() {
      return this.accuracyClass === ACCURACY_CLASS.WARNING;
    },
  },
  methods: {
    formattedCoordinates(coordinates) {
      if (!coordinates) return "";
      const [lon, lat] = coordinates;
      return `${lat.toFixed(6)}, ${lon.toFixed(6)}`;
    },
    formattedDate(isoDate) {
      if (!isoDate) return "";
      const parsed = new Date(isoDate);
      if (Number.isNaN(parsed.getTime())) return isoDate;
      return parsed.toLocaleString();
    },
  },
};
</script>

<style scoped lang="scss">
.terrain-location-field {
  display: flex;
  flex-direction: column;
  gap: var(--terrain-space-xs);
  font-size: var(--terrain-font-body);
}

.terrain-location-field-label {
  font-weight: 600;
  color: var(--color-navy);
}

.terrain-location-field-empty {
  color: var(--color-ghost);
  font-style: italic;
}

.terrain-location-field-row {
  display: flex;
  justify-content: space-between;
  gap: var(--terrain-space-md);
}

.terrain-location-field-key {
  color: var(--color-ghost);
}

.terrain-location-field-value {
  font-weight: 500;
  color: var(--color-graphite);
  display: flex;
  align-items: center;
  gap: var(--terrain-space-xs);
}

.terrain-location-field-badge {
  display: inline-block;
  padding: 2px var(--terrain-space-sm);
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--color-white);
}

.terrain-location-field-badge--manual { background: var(--color-steel); }
.terrain-location-field-badge--buena { background: var(--color-success); }
.terrain-location-field-badge--aceptable { background: var(--color-warning); }
.terrain-location-field-badge--advertida { background: var(--color-danger); }

.terrain-location-field-warning {
  background: var(--terrain-color-warning-light);
  color: var(--color-warning);
  border: 1px solid var(--color-warning);
  border-radius: var(--terrain-radius-md);
  padding: var(--terrain-space-sm) var(--terrain-space-md);
  font-size: 0.85rem;
}

.terrain-location-field-correction {
  border-top: 1px dashed var(--color-line);
  padding-top: var(--terrain-space-xs);
  margin-top: var(--terrain-space-xs);
}

.terrain-location-field-actions {
  display: flex;
  gap: var(--terrain-space-sm);
  margin-top: var(--terrain-space-xs);
}

.terrain-location-field-correct-btn,
.terrain-location-field-retry-btn {
  border: 1px solid var(--color-steel);
  background: var(--color-white);
  color: var(--color-navy);
  border-radius: var(--terrain-radius-xl);
  padding: var(--terrain-space-xs) var(--terrain-space-md);
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 600;
  transition: background-color var(--terrain-transition), color var(--terrain-transition);

  &:hover {
    background: var(--color-navy);
    border-color: var(--color-navy);
    color: var(--color-white);
  }
}
</style>
